/**
 * SimpleChat - ChatGPT Classic Style Interface
 * Provides a familiar AI chat experience for users
 */
import {
 useState, useRef, useEffect, useCallback 
} from 'react';
import {
  Box,
  TextField,
  IconButton,
  Typography,
  Avatar,
  Paper,
  Alert,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import SendIcon from '@mui/icons-material/Send';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import RouteIcon from '@mui/icons-material/Route';
import AssessmentIcon from '@mui/icons-material/Assessment';
import NotificationsIcon from '@mui/icons-material/Notifications';
import { useTranslation } from '../common/components/LocalizationProvider';
import { useSelector } from 'react-redux';
import { usePremiumTokens } from '../common/theme/usePremiumTokens';

// Use native Java endpoint
const ASSISTANT_API = '/api/assistant';

// Typing indicator component with CSS animation
const TypingIndicator = ({ tokens }) => {
  const dotStyle = {
    width: 8,
    height: 8,
    borderRadius: '50%',
    backgroundColor: tokens.text.muted,
    display: 'inline-block',
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, p: 1 }}>
      <span style={{ ...dotStyle, animation: 'typingDot 1.4s infinite 0s' }} />
      <span style={{ ...dotStyle, animation: 'typingDot 1.4s infinite 0.2s' }} />
      <span style={{ ...dotStyle, animation: 'typingDot 1.4s infinite 0.4s' }} />
      <style>
        {`
          @keyframes typingDot {
            0%, 60%, 100% { opacity: 0.3; transform: scale(1); }
            30% { opacity: 1; transform: scale(1.2); }
          }
        `}
      </style>
    </Box>
  );
};

// Suggestion chips for empty state
const SUGGESTIONS = [
  { icon: <DirectionsCarIcon />, text: 'Quais veículos estão online agora?' },
  { icon: <RouteIcon />, text: 'Mostre as viagens de hoje' },
  { icon: <AssessmentIcon />, text: 'Resumo do consumo de combustível' },
  { icon: <NotificationsIcon />, text: 'Quais alertas tivemos hoje?' },
];

const SimpleChat = () => {
  const theme = useTheme();
  const t = useTranslation();
  const tokens = usePremiumTokens();
  const chatRef = useRef(null);
  const inputRef = useRef(null);
  const user = useSelector((state) => state.session.user);

  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null);
  const [error, setError] = useState(null);

  // AI gradient using premium tokens
  const aiGradient = `linear-gradient(135deg, ${tokens.semantic.success} 0%, ${tokens.accent.primary} 100%)`;

  // Fetch assistant status on mount
  useEffect(() => {
    const fetchStatus = async () => {
      try {
        const response = await fetch(ASSISTANT_API, {
          credentials: 'include',
        });
        if (response.ok) {
          const data = await response.json();
          setStatus(data);
          if (!data.configured) {
            setError('Assistente não configurado. Solicite ao administrador.');
          } else if (!data.enabled) {
            setError('Assistente desabilitado pelo administrador.');
          }
        }
      } catch (e) {
        console.error('Error fetching status:', e);
        setError('Não foi possível conectar ao assistente');
      }
    };
    fetchStatus();
  }, []);

  // Auto-scroll to bottom
  useEffect(() => {
    if (chatRef.current) {
      chatRef.current.scrollTop = chatRef.current.scrollHeight;
    }
  }, [messages, loading]);

  const sendMessage = useCallback(async (messageText) => {
    const text = messageText || input;
    if (!text.trim() || loading) return;

    const userMessage = { role: 'user', content: text };
    setMessages((prev) => [...prev, userMessage]);
    setInput('');
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${ASSISTANT_API}/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          messages: [...messages, userMessage],
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }

      const data = await response.json();
      setMessages((prev) => [
        ...prev,
        { role: 'assistant', content: data.response },
      ]);
    } catch (e) {
      console.error('Chat error:', e);
      setError(`Erro: ${e.message}`);
      setMessages((prev) => [
        ...prev,
        { role: 'assistant', content: 'Desculpe, ocorreu um erro. Por favor, tente novamente.' },
      ]);
    } finally {
      setLoading(false);
      inputRef.current?.focus();
    }
  }, [input, loading, messages]);

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const handleSuggestionClick = (text) => {
    sendMessage(text);
  };

  // Get user initials for avatar
  const getUserInitials = () => {
    if (user?.name) {
      return user.name.split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2);
    }
    return 'U';
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        bgcolor: tokens.surface.background,
      }}
    >
      {/* Error Alert */}
      {error && (
        <Alert
          severity="error"
          onClose={() => setError(null)}
          sx={{ m: 1, borderRadius: tokens.radius.md }}
        >
          {error}
        </Alert>
      )}

      {/* Messages Area */}
      <Box
        ref={chatRef}
        sx={{
          flex: 1,
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Empty State / Welcome */}
        {messages.length === 0 && (
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              px: 3,
              py: 4,
            }}
          >
            {/* Logo/Icon */}
            <Box
              sx={{
                width: 72,
                height: 72,
                borderRadius: '50%',
                background: aiGradient,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 3,
                boxShadow: `0 4px 20px ${tokens.semantic.success}4D`,
              }}
            >
              <AutoAwesomeIcon sx={{ fontSize: 36, color: tokens.text.inverse }} />
            </Box>

            <Typography
              variant="h5"
              fontWeight={600}
              sx={{ mb: 1, color: tokens.text.primary }}
            >
              Como posso ajudar?
            </Typography>

            <Typography
              variant="body2"
              sx={{ mb: 4, color: tokens.text.muted, textAlign: 'center', maxWidth: 400 }}
            >
              Sou seu assistente de gestão de frotas. Posso ajudar com informações sobre veículos, viagens, alertas e muito mais.
            </Typography>

            {/* Suggestion Cards */}
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, 1fr)',
                gap: 2,
                width: '100%',
                maxWidth: 600,
              }}
            >
              {SUGGESTIONS.map((suggestion, index) => (
                <Paper
                  key={index}
                  onClick={() => handleSuggestionClick(suggestion.text)}
                  elevation={0}
                  sx={{
                    p: 2,
                    cursor: 'pointer',
                    borderRadius: tokens.radius.lg,
                    border: `1px solid ${tokens.border.default}`,
                    bgcolor: tokens.surface.card,
                    transition: tokens.transitions?.all || 'all 0.2s',
                    '&:hover': {
                      bgcolor: tokens.surface.hover,
                      borderColor: tokens.border.strong,
                      transform: 'translateY(-2px)',
                    },
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box sx={{ color: tokens.text.muted }}>
                      {suggestion.icon}
                    </Box>
                    <Typography
                      variant="body2"
                      sx={{ color: tokens.text.primary }}
                    >
                      {suggestion.text}
                    </Typography>
                  </Box>
                </Paper>
              ))}
            </Box>
          </Box>
        )}

        {/* Chat Messages */}
        {messages.map((message, index) => (
          <Box
            key={index}
            sx={{
              py: 3,
              px: 2,
              bgcolor: message.role === 'assistant'
                ? tokens.surface.elevated
                : 'transparent',
              borderBottom: `1px solid ${tokens.border.subtle}`,
            }}
          >
            <Box
              sx={{
                maxWidth: 800,
                mx: 'auto',
                display: 'flex',
                gap: 2,
              }}
            >
              {/* Avatar */}
              <Avatar
                sx={{
                  width: 36,
                  height: 36,
                  flexShrink: 0,
                  ...(message.role === 'assistant'
                    ? {
                        background: aiGradient,
                        boxShadow: `0 2px 8px ${tokens.semantic.success}40`,
                      }
                    : {
                        bgcolor: tokens.accent.primary,
                      }),
                }}
              >
                {message.role === 'assistant' ? (
                  <AutoAwesomeIcon sx={{ fontSize: 20 }} />
                ) : (
                  <Typography variant="body2" fontWeight={600}>
                    {getUserInitials()}
                  </Typography>
                )}
              </Avatar>

              {/* Message Content */}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  variant="subtitle2"
                  fontWeight={600}
                  sx={{
                    mb: 0.5,
                    color: tokens.text.primary,
                  }}
                >
                  {message.role === 'assistant' ? 'Assistente' : (user?.name || 'Você')}
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    color: tokens.text.secondary,
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    lineHeight: 1.7,
                  }}
                >
                  {message.content}
                </Typography>
              </Box>
            </Box>
          </Box>
        ))}

        {/* Typing Indicator */}
        {loading && (
          <Box
            sx={{
              py: 3,
              px: 2,
              bgcolor: tokens.surface.elevated,
            }}
          >
            <Box
              sx={{
                maxWidth: 800,
                mx: 'auto',
                display: 'flex',
                gap: 2,
              }}
            >
              <Avatar
                sx={{
                  width: 36,
                  height: 36,
                  background: aiGradient,
                  boxShadow: `0 2px 8px ${tokens.semantic.success}40`,
                }}
              >
                <AutoAwesomeIcon sx={{ fontSize: 20 }} />
              </Avatar>
              <Box>
                <Typography
                  variant="subtitle2"
                  fontWeight={600}
                  sx={{ mb: 0.5, color: tokens.text.primary }}
                >
                  Assistente
                </Typography>
                <TypingIndicator tokens={tokens} />
              </Box>
            </Box>
          </Box>
        )}
      </Box>

      {/* Input Area */}
      <Box
        sx={{
          p: 2,
          bgcolor: tokens.surface.background,
          borderTop: `1px solid ${tokens.border.subtle}`,
        }}
      >
        <Box sx={{ maxWidth: 800, mx: 'auto' }}>
          <Paper
            elevation={0}
            sx={{
              display: 'flex',
              alignItems: 'flex-end',
              borderRadius: tokens.radius.lg,
              border: `1px solid ${tokens.border.default}`,
              bgcolor: tokens.surface.card,
              overflow: 'hidden',
            }}
          >
            <TextField
              inputRef={inputRef}
              fullWidth
              multiline
              maxRows={6}
              placeholder={t('assistantPlaceholder') || 'Envie uma mensagem...'}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyPress={handleKeyPress}
              disabled={loading || !status?.configured}
              variant="standard"
              slotProps={{
                input: {
                  disableUnderline: true,
                  style: {
                    padding: '16px',
                    paddingRight: '8px',
                    color: tokens.text.primary,
                  },
                },
              }}
            />
            <IconButton
              onClick={() => sendMessage()}
              disabled={!input.trim() || loading || !status?.configured}
              sx={{
                m: 1,
                p: 1,
                bgcolor: input.trim() ? tokens.semantic.success : 'transparent',
                color: input.trim() ? tokens.text.inverse : tokens.border.default,
                borderRadius: tokens.radius.md,
                transition: tokens.transitions?.all || 'all 0.2s',
                '&:hover': {
                  bgcolor: input.trim() ? tokens.accent.hover : tokens.surface.hover,
                },
                '&:disabled': {
                  color: tokens.border.default,
                  bgcolor: 'transparent',
                },
              }}
            >
              <SendIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </Paper>

          {/* Footer */}
          <Typography
            variant="caption"
            sx={{
              display: 'block',
              textAlign: 'center',
              mt: 1.5,
              color: tokens.text.muted,
            }}
          >
            O assistente pode cometer erros. Verifique informações importantes.
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default SimpleChat;
